import {
  CheckIcon,
  EditIcon,
  ErrorIcon,
  SyncIcon,
  TrashIcon,
} from 'components/Icons';
import { clsx } from 'helpers/utils/HTMLUtils';
import { getUniqueKey } from 'helpers/utils/KeyUtils';
import translation from 'locales/translation';
import {
  Button,
  ButtonThemes,
  Collapse,
  IconButton,
} from 'plataforma-fundacao-componentes';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import './styles.scss';

interface EditableItemProps {
  assunto: string;
  message: string;
  disabled?: boolean;
  assuntoProps?: {
    placeholder?: string;
    maxLength?: number;
    validations?: ((val: any) => { error: boolean; helperText?: string })[];
  };
  messageProps?: {
    placeholder?: string;
    maxLength?: number;
    validations?: ((val: any) => { error: boolean; helperText?: string })[];
  };
  onRemoveClick: () => Promise<any>;
  onEditConfirm: (assunto: string, message: string) => Promise<any>;
}

const rootClassName = 'editable-item';

const EditableItem: React.FC<EditableItemProps> = ({ ...props }) => {
  const [id] = useState(getUniqueKey());
  const [assunto, setAssunto] = useState<string>(props.assunto);
  const [message, setMessage] = useState<string>(props.message);
  const [editable, setEditable] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [errorAssunto, setErrorAssunto] = useState<{
    error: boolean;
    helperText?: string;
  } | null>(null);
  useLayoutEffect(() => {
    const finded = props.assuntoProps?.validations?.find((func) => {
      return func(assunto).error === true;
    });
    setErrorAssunto(
      editable && typeof finded === 'function' ? finded!(assunto) : null
    );
  }, [assunto, editable, props.assuntoProps]);

  const [errorMessage, setErrorMessage] = useState<{
    error: boolean;
    helperText?: string;
  } | null>(null);
  useLayoutEffect(() => {
    const finded = props.messageProps?.validations?.find((func) => {
      return func(message).error === true;
    });
    setErrorMessage(
      editable && typeof finded === 'function' ? finded!(message) : null
    );
  }, [message, editable, props.messageProps]);

  useEffect(() => {
    const inputArea = document.querySelector(
      `#${id} textarea`
    ) as HTMLTextAreaElement;
    if (inputArea) {
      inputArea.style.height = `auto`;
      inputArea.style.height = `${inputArea.scrollHeight}px`;
    }
  }, [id, props.message]);

  return (
    <div
      id={`${id}`}
      className={editable ? `${rootClassName} editable` : `${rootClassName}`}
    >
      <div className={`${rootClassName}-content`}>
        <div>
          <input
            className={clsx([
              `${rootClassName}-input-area`,
              errorAssunto?.error ? 'error' : '',
              editable ? 'editable' : '',
            ])}
            value={assunto}
            disabled={loading}
            placeholder={props.assuntoProps?.placeholder}
            maxLength={props.assuntoProps?.maxLength}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setAssunto(e.target.value)
            }
            readOnly={!editable}
          />
          {editable && (
            <div className={`${rootClassName}-counter`}>
              {`${String(assunto || '').length}${
                props.assuntoProps?.maxLength ||
                props.assuntoProps?.maxLength === 0
                  ? `/${props.assuntoProps?.maxLength}`
                  : ''
              }`}
            </div>
          )}
          <Collapse opened={editable}>
            <span className={`${rootClassName}-input-error`}>
              {errorAssunto?.helperText}
            </span>
          </Collapse>
        </div>
        <div>
          <textarea
            id={`${id}`}
            className={clsx([
              `${rootClassName}-input-area description`,
              errorMessage?.error ? 'error' : '',
              editable ? 'editable' : '',
            ])}
            value={message}
            disabled={loading}
            maxLength={props.messageProps?.maxLength}
            placeholder={props.messageProps?.placeholder}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setMessage(e.target.value)
            }
            readOnly={!editable}
          />
          {editable && (
            <div className={`${rootClassName}-counter`}>
              {`${String(message || '').length}${
                props.messageProps?.maxLength ||
                props.messageProps?.maxLength === 0
                  ? `/${props.messageProps?.maxLength}`
                  : ''
              }`}
            </div>
          )}
          <Collapse opened={editable}>
            <span className={`${rootClassName}-input-error`}>
              {errorMessage?.helperText}
            </span>
          </Collapse>
        </div>
      </div>
      <div
        className={
          editable
            ? `${rootClassName}-actions editable`
            : `${rootClassName}-actions`
        }
      >
        {loading && !editable ? (
          <SyncIcon animated />
        ) : editable ? (
          <>
            <Button
              onClick={() => {
                setAssunto(props.assunto);
                setMessage(props.message);
                setEditable(false);
              }}
              leftIcon={<ErrorIcon />}
              theme={ButtonThemes.Secondary}
            >
              {translation.Parâmetros.modalResultadoPadrao.botaoCancelar}
            </Button>
            <Button
              type={'submit'}
              leftIcon={<CheckIcon />}
              theme={ButtonThemes.Primary}
              onClick={() => {
                setLoading(true);
                props.onEditConfirm(assunto, message).finally(() => {
                  setLoading(false);
                  setEditable(false);
                });
              }}
              loading={loading}
              disabled={!assunto || !message}
            >
              {translation.Parâmetros.modalResultadoPadrao.botaoSalvar}
            </Button>
          </>
        ) : (
          <div className={`${rootClassName}-actions-inside`}>
            <IconButton icon={<EditIcon />} onClick={() => setEditable(true)} />
            <IconButton
              icon={<TrashIcon />}
              onClick={() => {
                setLoading(true);
                props.onRemoveClick!()?.finally(() => {
                  setLoading(false);
                });
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EditableItem;
